"use strict";

import React, {
  createContext,
  useContext,
  useMemo,
  useEffect,
  useLayoutEffect,
  useRef,
} from "react";
import { signal, batch } from "@preact/signals";
import { render } from "react-dom";
import classNames from "classnames/bind";
import {
  E,
  appData,
  useLearningConditions,
  useLearningBeliefs,
  useDisaggregations,
} from "./bel_shared2.js";
import { BelNavAux, links } from "./nav.js";
import { PopupGraph } from "./graph_full.js";
import { GlyphClose, GlyphDownload, GlyphVisit } from "./icons.js";
import { AbstractTrackURL, RawScore, SurveyAccordion } from "./components.js";
import { InfoPopup, RawPercent, ToggleGroup } from "./bel_v2_components.js";
import {
  EXPLORE_RESPONSES,
  CONSIDER_EXPERIENCES,
  TAKE_ACTION,
} from "./bel_v2_popups.js";

const AppState = createContext();

function createAppState() {
  const appState = {
    selectedMeasureSlug: signal(),
    activeSectionKey: signal(),
    lensViewOpen: signal(),
    lensTabSlug: signal(),
    expandedItemSlugs: signal([]),
    selectedTabSlug: signal(),
  };
  const tabStates = signal({});
  appState.stateForTab = (slug) => {
    let tabState = tabStates.value[slug];
    if (!tabState) {
      tabState = {
        historyModeSlug: signal(),
        selectedCompKey: signal(),
        selectedOptKey: signal(),
        selectedObjectSlug: signal(),
      };
      tabStates.value = { ...tabStates.value, [slug]: tabState };
    }
    return tabState;
  };
  return appState;
}

class TrackURL extends AbstractTrackURL {
  static basePathname = links.lc_template.replace("_PLACEHOLDER_/", "");

  state = {};

  updateAppState(urlParts, initialCtx) {
    let {
      path: [measure],
      fragPath: [section, tab],
      fragParams: {
        tour,
        _tour_nav,
        i: items,
        l: lensView,
        h: historyMode,
        c: cmpKey,
        d: optKey,
        s: selObj,
      },
    } = urlParts;

    // WORKAOUND: this avoids the "disappearing tours" bug we've been encountering (#424)
    if (_tour_nav) tour = _tour_nav;

    if (tour || !initialCtx) {
      if (window.impact_tours) window.impact_tours.setToken(tour || "");
    }

    const appState = initialCtx || this.props.appState;
    if (measure) {
      appState.selectedMeasureSlug.value = measure;
      if (lensView) {
        appState.lensViewOpen.value = true;
        appState.lensTabSlug.value = lensView;
      } else {
        appState.lensViewOpen.value = false;
        appState.lensTabSlug.value = null;
      }
      if (section) {
        appState.activeSectionKey.value = section;
        if (section === "responses") {
          appState.expandedItemSlugs.value = items ? items.split(",") : [];
        } else if (section === "differences") {
          appState.selectedTabSlug.value = tab;

          const tabState = appState.stateForTab(tab);
          tabState.historyModeSlug.value = historyMode;
          tabState.selectedCompKey.value = cmpKey;
          tabState.selectedOptKey.value = optKey;
          tabState.selectedObjectSlug.value = selObj;
        }
      }
    }
  }

  generateUrlParts() {
    const appState = this.props.appState;

    let measure = appState.selectedMeasureSlug.value;
    let lensView = appState.lensViewOpen.value;
    let lensTabSlug = appState.lensTabSlug.value;
    let section = appState.activeSectionKey.value;

    let path = [measure, ""];
    let fragPath = [section];

    let { toursToken } = this.state;
    let fragParams = {};
    if (toursToken) {
      fragParams["tour"] = toursToken;
    }
    if (lensView) {
      fragParams["l"] = lensTabSlug || "_";
    }
    if (section === "responses") {
      let expandedItemSlugs = appState.expandedItemSlugs.value;
      if (expandedItemSlugs.length) {
        fragParams["i"] = expandedItemSlugs.join(",");
      }
    } else if (section === "differences") {
      let selectedTabSlug = appState.selectedTabSlug.value;
      if (selectedTabSlug) {
        fragPath.push(selectedTabSlug);

        const tabState = appState.stateForTab(selectedTabSlug);
        let historyModeSlug = tabState.historyModeSlug.value;
        if (historyModeSlug) {
          fragParams["h"] = historyModeSlug;
        }
        let selectedCompKey = tabState.selectedCompKey.value;
        if (selectedCompKey) {
          fragParams["c"] = selectedCompKey;
        }
        let selectedOptKey = tabState.selectedOptKey.value;
        if (selectedOptKey) {
          fragParams["d"] = selectedOptKey;
        }
        let selectedObjectSlug = tabState.selectedObjectSlug.value;
        if (selectedObjectSlug) {
          fragParams["s"] = selectedObjectSlug;
        }
      }
    }

    return { path, fragPath, fragParams };
  }
}

const LearningBeliefs = createContext([]);

const BarGraph = ({ value: v }) => (
  <div class="bar-graph">
    <span class="bar-color" style={{ width: `${v || 0}%` }} />
    <span class="bar-label">
      <RawPercent value={v} />
    </span>
  </div>
);

const LensView = ({ condition: lc, belief: lb }) => {
  const lbInfo = useMemo(
    () => lc.lenses.find((d) => d.slug === lb.slug) || {},
    [lc, lb],
  );

  return (
    <div class="lens-view">
      <p>
        Students who report experiencing <b>High {lc.name}</b> are more likely
        to say “{lb.name}” than students who report <b>Low {lc.name}</b>.
      </p>
      <p>
        Of the students who report <b>Low {lc.name}</b>,{" "}
        <RawPercent value={lbInfo.lo} /> report saying “{lb.name}”.
        <BarGraph value={lbInfo.lo} />
      </p>
      <p>
        Of the students who report <b>High {lc.name}</b>,{" "}
        <RawPercent value={lbInfo.hi} /> report saying “{lb.name}”.
        <BarGraph value={lbInfo.hi} />
      </p>
    </div>
  );
};

const LensPopup = ({ measure, onclose }) => {
  const ref = useRef();
  useEffect(() => {
    ref.current.showModal();
    return () => {
      ref.current.close();
    };
  }, []);

  const lbEssential = useContext(LearningBeliefs);
  const lbObjects = lbEssential.measures;
  const { lensTabSlug } = useContext(AppState);
  const activeObject = useMemo(
    () => lbObjects.find((d) => d.slug === lensTabSlug.value) || lbObjects[0],
    [lbObjects, lensTabSlug.value],
  );

  const isSafari = useMemo(() => navigator.vendor.indexOf("Apple") === 0, []);

  return (
    <dialog
      ref={ref}
      class={classNames("lens-popup", {
        "hide-autofocus": isSafari,
      })}
      onclose={onclose}
    >
      <button
        type="button"
        class="close-button"
        onclick={() => {
          ref.current.close();
        }}
        aria-label="Close"
      >
        <GlyphClose />
      </button>
      <h3>
        {measure.name}’s Connections to {lbEssential.name}
      </h3>
      <p class="call-to-action">
        See how important positive experiences of {measure.name} are for
        students’ {lbEssential.name}.
      </p>
      <div role="tablist" class="lens-tab-options">
        {lbObjects.map((d) => (
          <button
            type="button"
            role="tab"
            aria-selected={d.slug === activeObject.slug}
            aria-controls="active-lens-ui"
            onclick={(evt) => {
              lensTabSlug.value = d.slug;
              evt.preventDefault();
            }}
          >
            {d.name}
          </button>
        ))}
      </div>
      <div id="active-lens-ui">
        <LensView condition={measure} belief={activeObject} />
      </div>
      <p>
        See all the survey response data for {lbEssential.name}:{" "}
        <a href={links.appendix} target="_blank">
          Learning Beliefs Appendix <GlyphVisit />
        </a>
      </p>
    </dialog>
  );
};

const MeasureOverview = ({ measure, guideResource }) => {
  const { lensViewOpen } = useContext(AppState);
  const lensViewEnabled = true;
  return (
    <div class="object-overview callout-box">
      <header>
        <h2 class="measure-name">{measure.name}</h2>
        <p class="score-label">
          Score: <RawScore result={measure._results.latest} />
        </p>
      </header>
      <div class="columns-wrapper">
        <div class="col col1">
          <div>
            <dt>What is {measure.name}?</dt>
            <dd dangerouslySetInnerHTML={measure.text.definition} />
          </div>
          {guideResource && (
            <p class="prompt dl-guide">
              For research and recommendations:
              <a href={guideResource.url}>
                Download the Guide <GlyphDownload />
              </a>
            </p>
          )}
        </div>
        <div class="col col2">
          <div>
            <dt>How does {measure.name} connect to student development?</dt>
            <dd dangerouslySetInnerHTML={measure.text.overview} />
          </div>
          {lensViewEnabled ? (
            <p class="prompt lb-appendix">
              <button
                type="button"
                class="button-appearance"
                onclick={(evt) => {
                  lensViewOpen.value = true;
                }}
              >
                Connection to Learning Beliefs <GlyphVisit />
              </button>
            </p>
          ) : (
            <p class="prompt lb-appendix">
              <a class="button-appearance disabled">
                Connection to Learning Beliefs <GlyphVisit />{" "}
                <span class="preview-note">(Coming Soon)</span>
              </a>
              In the meantime, visit the{" "}
              <a href={links.appendix} target="_blank">
                Learning Beliefs Appendix <GlyphVisit />
              </a>
            </p>
          )}
        </div>
      </div>
      {lensViewEnabled && lensViewOpen.value && (
        <LensPopup
          {...{ measure }}
          onclose={() => {
            lensViewOpen.value = false;
          }}
        />
      )}
    </div>
  );
};

const SectionsNav = ({ sections }) => {
  const ref = useRef();
  const { activeSectionKey } = useContext(AppState);
  const _localState = useRef();
  useEffect(() => {
    let _throttleTimeout = null;
    function scrollWatcher() {
      if (!_throttleTimeout) {
        _throttleTimeout = setTimeout(() => {
          _throttleTimeout = null;
          scrollHandler();
        }, 100);
      }
    }
    function scrollHandler() {
      // find last section w/top above our own element
      // (we assume document order matches visual order)
      let lastEl = null;
      const botY = ref.current.getBoundingClientRect().bottom;
      const secEls = document.querySelectorAll(".nav-section");
      for (const secEl of secEls) {
        const elY = secEl.getBoundingClientRect().top;
        if (elY > botY) {
          break;
        }
        lastEl = secEl;
      }
      const key = lastEl?.id;
      activeSectionKey.value = key;
      _localState.alreadyScrolledKey = key;
    }
    window.addEventListener("scroll", scrollWatcher, false);
    return () => {
      window.removeEventListener("scroll", scrollWatcher, false);
    };
  }, []);
  useLayoutEffect(() => {
    const key = activeSectionKey.value;
    if (key !== _localState.alreadyScrolledKey) {
      document.getElementById(key)?.scrollIntoView();
    }
  }, [activeSectionKey.value]);

  return (
    <nav class="scrollable-sections" {...{ ref }}>
      <ol>
        {sections.map((d) => (
          <li aria-selected={activeSectionKey.value === d.key}>
            <a
              href={`#${d.key}`}
              onclick={(evt) => {
                // trigger a scroll ourselves to avoid browser setting hash itself
                document.getElementById(d.key)?.scrollIntoView();
                evt.preventDefault();
              }}
            >
              {d.title}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
};

const SurveyDisaggregation = ({ survey, measure, activeTab }) => {
  const historyModes = [
    { slug: "now", label: "Current", view: "performance" },
    { slug: "2yr", label: "2-Year", view: "trends-bel" },
  ];

  const tabState = useContext(AppState).stateForTab(activeTab.slug);

  const { historyModeSlug } = tabState;
  const activeHistoryMode = useMemo(
    () =>
      historyModes.find((d) => d.slug === historyModeSlug.value) ||
      historyModes[0],
    [historyModes, historyModeSlug.value],
  );

  const comps = activeTab.options;
  const { selectedCompKey } = tabState;
  const activeFamily = useMemo(
    () => comps.find((d) => d.slug === selectedCompKey.value) || comps[0],
    [comps, selectedCompKey.value],
  );

  const opts = activeFamily.options;
  const { selectedOptKey } = tabState;
  const activeOption = useMemo(
    () => opts.find((d) => d.slug === selectedOptKey.value) || opts[0],
    [opts, selectedOptKey.value],
  );

  const comparisonObjects = useMemo(
    () => activeOption.soloObjects || [measure, ...activeOption.objects],
    [activeOption],
  );

  const { selectedObjectSlug } = tabState;
  const selectedObject = useMemo(
    () =>
      comparisonObjects.find((d) => d.slug === selectedObjectSlug.value) ||
      // ~HACK: `undefined` or a bogus slug select first object by default (#346),
      // but if `null` then that means user "closed" all selection via graph (#470)!
      (selectedObjectSlug.value !== null ? comparisonObjects[0] : null),
    [comparisonObjects, selectedObjectSlug.value],
  );

  // helpers to reset sub-selections on change
  const changeActiveFamily = (family) => {
    batch(() => {
      selectedCompKey.value = family.slug;
      selectedOptKey.value = undefined;
      selectedObjectSlug.value = undefined;
    });
  };
  const changeActiveOption = (option) => {
    batch(() => {
      selectedOptKey.value = option.slug;
      selectedObjectSlug.value = undefined;
    });
  };

  const idPrefix = `${measure.slug}-disaggregation/${activeTab.slug}`;
  const surveyToggleId = `${idPrefix}-surveyMode`;

  return (
    <div
      class="survey-disaggregation"
      data-history-mode={activeHistoryMode.slug}
    >
      <div class="view-options">
        <div class="history-mode">
          <span id={surveyToggleId} class="toggle-label">
            Survey:
          </span>{" "}
          <ToggleGroup
            aria-labelledby={surveyToggleId}
            items={historyModes}
            activeItem={activeHistoryMode}
            setActiveItem={(d) => {
              historyModeSlug.value = d.slug;
            }}
          />
        </div>
        <hr />
        <div class="disaggregations">
          <div class="main-disaggregation">
            <ToggleGroup
              items={comps}
              activeItem={activeFamily}
              setActiveItem={changeActiveFamily}
            />
          </div>
          {opts && (
            <div class="sub-disaggregation">
              <ToggleGroup
                items={opts}
                itemProps={(d) => ({
                  "data-missing-main": !d.hasMainData || null,
                  "data-missing-all": !d.hasAnyData || null,
                })}
                activeItem={activeOption}
                setActiveItem={changeActiveOption}
              />
            </div>
          )}
        </div>
      </div>
      <div
        class={classNames("data-view", {
          "wide-graph": activeHistoryMode.slug === "2yr",
        })}
      >
        <PopupGraph
          survey={survey}
          rankRanges={E.rankRanges}
          activeView={activeHistoryMode.view}
          objects={comparisonObjects}
          activeObject={measure}
          selectedObject={selectedObject}
          selectObject={(_lvl, slug) => {
            selectedObjectSlug.value = slug;
          }}
          triggerScrollHelper={(_) => _}
          dynamicWidth={false}
        />
        {activeHistoryMode.slug == "now" && (
          <table class="comparison-table">
            <thead>
              <tr>
                <td colspan="1" />
                <th>Score</th>
              </tr>
            </thead>
            <tbody>
              {comparisonObjects.map((d) => (
                <tr
                  class={classNames({ selected: d === selectedObject })}
                  onclick={(evt) => {
                    selectedObjectSlug.value = d.slug;
                  }}
                >
                  <td>{d.name}</td>
                  <td>
                    <RawScore
                      result={d._results.latest}
                      missing_label="Insufficient Data"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const MeasureResources = ({ measure }) => {
  const [summary, addlResources] = useMemo(() => {
    let summary = null;
    let resources = [];
    measure._resources.forEach((d) => {
      if (d._is_overview) summary = d;
      else resources.push(d);
    });
    return [summary, resources];
  }, [measure]);

  return (
    <div class="measure-resources callout-box">
      <h4>Here are resources to help you take action:</h4>
      <p class="dl-summary">
        {summary.action}:{" "}
        <a class="button-appearance" href={summary.url}>
          {measure.name} {summary.name} <GlyphDownload />
        </a>
      </p>
      <div class="addl-links">
        {addlResources.map((resource) => (
          <div class="link-group">
            <dt>{resource.action}:</dt>
            <dd>
              <a href={resource.url}>
                {resource.name}{" "}
                {resource.icon === "download" ? (
                  <GlyphDownload />
                ) : (
                  <GlyphVisit />
                )}
              </a>
            </dd>
          </div>
        ))}
      </div>
    </div>
  );
};

const MeasureDetails = ({ survey, measure }) => {
  const { expandedItemSlugs } = useContext(AppState);
  const sections = [
    { key: "responses", title: "Explore Overall Responses" },
    {
      key: "differences",
      tabLabelId: "differences-heading",
      title: "Consider Different Experiences",
    },
    { key: "actions", title: "Take Action" },
  ];

  const tabs = useDisaggregations(survey, measure);
  const { selectedTabSlug } = useContext(AppState);
  const activeTab = useMemo(
    () => tabs.find((d) => d.slug === selectedTabSlug.value) || tabs[0],
    [tabs, selectedTabSlug.value],
  );
  // NOTE: this one's a bit more critical to patch up due to its nested state
  if (activeTab.slug !== selectedTabSlug.value) {
    selectedTabSlug.value = activeTab.slug;
  }

  const guideResource = useMemo(
    () => measure._resources.find((d) => d._is_guide),
    [measure],
  );

  return (
    <div>
      <MeasureOverview {...{ measure, guideResource }} />
      <SectionsNav {...{ sections }} />
      <div id={sections[0].key} class="nav-section">
        <h3>{sections[0].title}</h3>
        <p class="call-to-action">
          <InfoPopup children={EXPLORE_RESPONSES} />
          See what students are saying about {measure.name} in one of their
          classes.
        </p>
        <p class="addl-context">
          Questions are in order of easiest to hardest to achieve in a
          classroom.
        </p>
        <SurveyAccordion
          survey={survey}
          activeView="trends"
          showDots={false}
          showHistory={false}
          showBenchmarks={false}
          activeObject={measure}
          childObjects={measure.items}
          expandedItems={expandedItemSlugs.value}
          updateItems={(arr) => {
            expandedItemSlugs.value = arr;
          }}
          StemEl="p"
          stemClasses="intro-stem"
          newCultivateStyle={true}
        />
      </div>

      <hr class="section-divider" />

      <div id={sections[1].key} class="nav-section">
        <h3 id={sections[1].tabLabelId}>{sections[1].title}</h3>
        <p class="call-to-action">
          <InfoPopup children={CONSIDER_EXPERIENCES} />
          See variation in student responses across classrooms and within
          demographics.
        </p>
        <div
          role="tablist"
          aria-labelledby={sections[1].tabLabelId}
          class="disaggregation-tab-options"
        >
          {tabs.map((d) => (
            <button
              type="button"
              role="tab"
              aria-selected={d.slug === activeTab.slug}
              aria-controls="active-disaggregator-ui"
              onclick={(evt) => {
                selectedTabSlug.value = d.slug;
                evt.preventDefault();
              }}
            >
              {d.label}
            </button>
          ))}
        </div>
        <div id="active-disaggregator-ui">
          <SurveyDisaggregation
            key={activeTab.slug}
            {...{ survey, measure, activeTab }}
          />
        </div>
      </div>

      <hr class="section-divider" />

      <div id={sections[2].key} class="nav-section">
        <h3>{sections[2].title}</h3>
        <p class="call-to-action">
          <InfoPopup children={TAKE_ACTION} />
          Use the findings to improve students’ experience of {measure.name}.
        </p>
        <p class="addl-context">
          Cultivate is a starting point for sparking important conversations
          with students and colleagues about how to improve students’
          experiences.
        </p>
        <MeasureResources {...{ measure }} />
      </div>
    </div>
  );
};

const BelReport = ({ survey, target, essentials, measures }) => {
  const [lcEssential, lcMeasures] = useLearningConditions(essentials, measures);
  const [lbEssential] = useLearningBeliefs(essentials, measures);
  const { selectedMeasureSlug, activeSectionKey } = useContext(AppState);
  const selectedMeasure = useMemo(
    () =>
      lcMeasures.find((d) => d.slug === selectedMeasureSlug.value) ||
      lcMeasures[0],
    [lcMeasures, selectedMeasureSlug.value],
  );

  return (
    <div id="report-body" class="columns-wrapper">
      <div class="sidebar">
        <h1 class="combo-heading">
          <a class="target-name" href={links.target_landing}>
            {target.name}
          </a>{" "}
          <span class="page-name">{lcEssential.name}</span>
        </h1>
        <ul class="measures-table" role="tablist">
          {lcMeasures.map((m) => (
            <li
              key={m.slug}
              class="measure"
              role="tab"
              aria-controls="active-measure-details"
              aria-selected={m === selectedMeasure}
              onClick={(evt) => {
                evt.preventDefault();

                // reset current scrollable area whenever clicked (even if unchanged)
                activeSectionKey.value = null;
                document.documentElement.scrollTo({
                  top: 0,
                  behavior: m === selectedMeasure ? "auto" : "instant",
                });
                selectedMeasureSlug.value = m.slug;
              }}
            >
              {m.name}
            </li>
          ))}
        </ul>
      </div>
      <LearningBeliefs.Provider value={lbEssential}>
        <div id="active-measure-details" class="details">
          <MeasureDetails
            key={selectedMeasure.slug}
            survey={survey}
            measure={selectedMeasure}
          />
        </div>
      </LearningBeliefs.Provider>
    </div>
  );
};

const appState = createAppState();
TrackURL.intializeState(appState);

const AppUrlWrapper = () => {
  const appState = useContext(AppState);
  const tabState = appState.stateForTab(appState.selectedTabSlug.value);
  // HACK: we use all the values to trigger our own re-render…
  Object.values(appState).map((d) => void d.value);
  Object.values(tabState).map((d) => void d.value);
  // …and make a copy of `appState` to trigger subcomponent's re-render.
  return (
    <TrackURL
      ref={
        window.impact_tours
          ? (tracker) => {
              window.impact_tours.watchToken((token) => {
                if (tracker) tracker.setState({ toursToken: token });
              });
            }
          : null
      }
      appState={{ ...appState }}
    />
  );
};

render(
  <AppState.Provider value={appState}>
    <BelReport {...appData} />
    <AppUrlWrapper />
  </AppState.Provider>,
  document.querySelector("#react_target"),
);

render(<BelNavAux />, document.querySelector("#react_bel_aux_nav"));
